<template>
  <div>
    <div
      v-if="isLoading"
      class="flex items-center gap-[8px] justify-center flex-wrap py-[24px]"
    >
      <b-spinner
        small
        variant="primary"
      />
      <span class="text-[#626262] text-[12px] m-0">memuat data...</span>
    </div>

    <div
      v-if="isError && !isLoading"
      class="py-[24px]"
    >
      <div class="flex items-center gap-[8px] justify-center flex-wrap mb-[8px]">
        <span class="k-danger text-[#E31A1A] border-[1px] border-[#E31A1A] bg-[#FFEDED] rounded-[4px] size-[24px] flex items-center justify-center rounded-full text-[14px] m-0" />
        <span class="text-[#626262] text-[12px] m-0">Gagal mengambil data, silahkan coba lagi</span>
      </div>
      <div class="flex items-center gap-[8px] justify-center flex-wrap">
        <b-button
          variant="primary"
          size="sm"
          @click="handleRetry"
        >
          Coba Lagi
        </b-button>
      </div>
    </div>

    <div
      v-if="!isLoading && !isError"
      class="flex items-start justify-around"
    >
      <product-card :products="dataDetail.products" />
      <collapse-body-card label="Alamat Penerima">
        {{ dataDetail.recipient_address }}
      </collapse-body-card>
      <collapse-body-card
        label="Biaya Fullfullment"
        container-class="min-w-[180px]"
      >
        {{ usedFulfillmentFee | rupiah }}
      </collapse-body-card>
      <collapse-body-card
        label="Bahan Packing"
        container-class="min-w-[180px]"
      >
        <list-packing-materials :items="packingMaterials" />
        <b-button
          variant="ghost"
          size="sm"
          class="rounded-lg !text-[#F95031] !font-normal !px-[14px] !text-[12px]"
          :disabled="!isEditPackingEnabled"
          @click="handleEditPacking"
        >
          + Bahan Packing
        </b-button>
      </collapse-body-card>

      <ModalPackagingSettings
        ref="modalPackagingSettings"
        :fetcher="packingMaterialsFetcher"
        @on-save="handleSavePacking"
      />
    </div>
  </div>
</template>

<script>
import { toastError } from '@/libs/toastification'
import useCacheableFetcher from '@/@core/libs/fetch/useCacheableFetcher'
import CollapseBodyCard from './CollapseBodyCard.vue'
import ListPackingMaterials from '../../packing/ListPackingMaterials.vue'
import ModalPackagingSettings from '../non-komship/detail/ModalPackagingSettings.vue'
import ProductCard from './ProductCard.vue'

export default {
  name: 'CollapseCardBody',
  components: {
    CollapseBodyCard,
    ListPackingMaterials,
    ModalPackagingSettings,
    ProductCard,
  },
  props: {
    packingMaterialsFetcher: {
      type: Object,
      required: false,
      default: () => ({}),
    },
    data: {
      type: Object,
      required: false,
      default: () => ({}),
    },
    isOpen: {
      type: Boolean,
      required: false,
      default: false,
    },
    id: {
      type: Number,
      required: false,
      default: null,
    },
    fulfillmentFee: {
      type: Number,
      required: false,
      default: 0,
    },
    editedOrderData: {
      type: Object,
      required: false,
      default: () => ({}),
    },
  },
  data() {
    return {
      detailFetcher: null,
      newPackingMaterials: undefined,
    }
  },
  computed: {
    isError() {
      return this.detailFetcher && (this.detailFetcher.state?.error || !this.detailFetcher?.state?.data)
    },
    isLoading() {
      return this.detailFetcher && this.detailFetcher.state?.isLoading
    },
    dataDetail() {
      return this.detailFetcher?.state?.data || {
        recipient_address: '-',
        packing_materials: [],
      }
    },
    quantity() {
      return this.dataDetail?.products?.reduce((acc, item) => acc + item.quantity, 0) || 0
    },
    packingMaterials() {
      return this.newPackingMaterials || this.editedOrderData[this.id]?.packings || this.dataDetail?.packing_materials
    },
    usedFulfillmentFee() {
      return this.editedOrderData[this.id]?.grand_fulfillment_cost || this.fulfillmentFee
    },
    isEditPackingEnabled() {
      return !this.$route.query.status || this.$route.query.status === 'submitted'
    },
  },
  watch: {
    isOpen(newVal) {
      if (newVal) {
        if (!this.id || !this.detailFetcher) {
          return
        }

        this.detailFetcher.refetch()
      }
    },
    id(newVal) {
      if (newVal) {
        if (!this.isOpen || !this.detailFetcher) {
          return
        }

        this.detailFetcher.refetch()
      }
    },
  },
  created() {
    this.initializeDetailFetcher()
  },
  mounted() {
    if (this.id && this.isOpen) {
      this.detailFetcher.refetch()
    }
  },
  methods: {
    handleSavePacking(message) {
      this.newPackingMaterials = message.data.packings
      this.$emit('on-save-packing', message)
    },
    handleEditPacking() {
      this.$refs.modalPackagingSettings.show({
        data: this.dataDetail,
        totalOrder: 1,
        position: this.id,
        packings: this.packingMaterials,
        fromMarketplace: true,
      })
    },
    handleOpenModalPackagingSettings(item, index) {
      this.$refs.modalPackagingSettings.show({
        fromMarketplace: true,
        data: item,
        totalOrder: 1,
        position: index,
        packings: item.packing_materials,
      })
    },
    // fetching the detail order
    async fetchDetail() {
      const res = await this.$http_new.get(`/komship/api/v1/marketplace-orders/${this.id}/mitras`)
      if (!res.data?.data) throw new Error('Data not found')
      return res.data?.data
    },
    // initializing the detail fetcher with the cacheable fetcher so that the data is cached and not fetched again
    initializeDetailFetcher() {
      const fetcher = useCacheableFetcher(['/komship/api/v1/marketplace-orders/', this.id, 'mitras'], this.fetchDetail, {
        immediate: false,
        staleTime: 1 * 60 * 1000,
      })
      this.detailFetcher = fetcher
    },
    // refetching the detail order
    async handleRefetchDetail() {
      try {
        await this.refetch()
      } catch (error) {
        toastError('Gagal mengambil data detail order, silahkan coba lagi')
      }
    },
    // retrying the detail order
    handleRetry() {
      this.detailFetcher.invalidate()
      this.detailFetcher.refetch()
    },
  },
}
</script>
